import React, { useContext, useState } from "react";
import {
  Activity,
  ArrowLeftRight,
  Bandage,
  BookMarked,
  CalendarDays,
  ChevronRight,
  Clock,
  Dumbbell,
  Flame,
  History,
  MapPin,
  NotebookPen,
  Pause,
  Target,
  UserRound,
  PersonStanding,
  Heart,
  HeartPulse,
  Ruler,
  Scale,
  Wind,
} from "lucide-react";
import { useAppState, WorkoutContext } from "../contexts/WorkoutContext";
import { useAuth } from "../contexts/AuthContext";
import { useUserProfile } from "../contexts/UserProfileContext";
import useWorkoutSettings from "../hooks/useWorkoutSettings";
import useSessionRecipes from "../hooks/useSessionRecipes";
import { openSetup } from "../utils/setupFlow";
import WeeklyTargetSummary from "../components/profile/WeeklyTargetSummary";
import ProfileSettingsSheet from "../components/profile/ProfileSettingsSheet";
import BenchmarksSheet from "../components/profile/BenchmarksSheet";
import RecipesSheet from "../components/profile/RecipesSheet";
import PlaceSheet from "../components/profile/PlaceSheet";
import EquipmentSetSheet from "../components/profile/EquipmentSetSheet";
import { AddRow, DefaultTag, Group, ItemRow, SummaryRow, ControlRow, Tag, MoreOptionsRow } from "../components/profile/ProfileParts";
import {
  describeChangeovers,
  describeDays,
  describeEquipmentSet,
  describeHistory,
  describeLevel,
  describePlace,
  describePaceAndRest,
  describeWarmCool,
  previewBenchmarks,
} from "../components/profile/profileSummaries";
import { heartRateBasis, zoneFloors } from "../utils/heartRateZones";
import { formatWeight, weightUnitOf } from "../utils/units";
import UnitToggle from "../components/ui/UnitToggle";
import { Vo2maxSheet, useVo2max } from "../components/profile/CardioSheets";
import { useBluetoothItems } from "../components/settings/BluetoothSheet";
import { useShowAdvanced } from "../hooks/useUiMode";
import { useSocial } from "../contexts/SocialContext";
import { useBadgesShown } from "../components/social/Badges";
import { LevelCard } from "../components/social/LevelShield";

/**
 * Profile (issue #86): one page of grouped rows, each showing what's set and
 * opening a sheet. No tabs and no Save buttons; a sheet saves when it closes.
 * Profile is what shapes the sessions Khronos writes; how the Coach plays
 * them back lives in Settings.
 */
const ProfileView = () => {
  const { userProfile, setUserProfile, setView } = useContext(WorkoutContext);
  const { setSettingsSheet } = useAppState();
  const { eq, items: btItems } = useBluetoothItems();
  const { user, isAuthenticated } = useAuth();
  const { streaks, loadStreaks } = useSocial();
  const badgesShown = useBadgesShown();
  // Fresh each visit: this week's ticks move as you train.
  React.useEffect(() => {
    if (isAuthenticated) loadStreaks?.();
  }, [isAuthenticated, loadStreaks]);
  const {
    profile: cloudProfile,
    performanceCapabilities = [],
    locationPresets = [],
    equipmentPresets = [],
  } = useUserProfile();
  const [workoutSettings] = useWorkoutSettings();
  const { recipes } = useSessionRecipes();

  // Which sheet is open: a settings kind, "benchmarks", "recipes", or
  // { place } / { kit } with an id or "new".
  const [sheet, setSheet] = useState(null);
  const close = () => setSheet(null);
  const settingsKind =
    typeof sheet === "string" && !["benchmarks", "recipes", "vo2"].includes(sheet) ? sheet : null;
  const benchmarks = isAuthenticated ? performanceCapabilities : [];
  // For formulas that differ by sex, such as the 2k VO2max.
  const sex = cloudProfile?.training_data?.sex || null;
  const vo2max = useVo2max({ signedIn: isAuthenticated, userProfile, benchmarks, sex });
  const places = isAuthenticated ? locationPresets : [];
  const kits = isAuthenticated ? equipmentPresets : [];
  const name = user?.displayName || user?.name || "";

  // Basic mode (#172): the rows most people never change stay tucked away
  // unless they already hold something. "More options" shows them all.
  const { show: showAll, reveal } = useShowAdvanced();
  const hasRecipes = recipes.length > 0;

  const aboutYou = (
    <Group
      title="You"
      footer={
        <WeeklyTargetSummary
          profile={userProfile}
          className="[&>p:first-child]:text-muted-foreground [&>p:first-child]:text-xs"
        />
      }
    >
      <SummaryRow Icon={UserRound} label="Age & level" value={describeLevel(userProfile)} onClick={() => setSheet("level")} />
      <SummaryRow
        Icon={PersonStanding}
        label="Sex"
        value={sex === "male" ? "Male" : sex === "female" ? "Female" : "Not set"}
        muted={!sex}
        onClick={() => setSheet("sex")}
      />
      <SummaryRow Icon={Target} label="Goal" value={userProfile.primaryGoal || "Not set"} muted={!userProfile.primaryGoal} onClick={() => setSheet("goal")} />
      <SummaryRow Icon={CalendarDays} label="Days a week" value={describeDays(userProfile)} onClick={() => setSheet("days")} />
      <SummaryRow
        Icon={Bandage}
        label="Injuries"
        value={userProfile.injuryNotes || "None"}
        muted={!userProfile.injuryNotes}
        onClick={() => setSheet("injuries")}
      />
      {/* Units (#191): kg | lb, switched in place. */}
      <ControlRow Icon={Ruler} label="Units">
        <UnitToggle
          value={weightUnitOf(userProfile)}
          onChange={(u) => setUserProfile({ ...userProfile, weightUnit: u })}
        />
      </ControlRow>
    </Group>
  );

  const howYouTrain = (
    <Group title="Sessions" note="Every new session starts here">
      <SummaryRow Icon={Clock} label="Session length" value={`${userProfile.timeAvailable || 45} min`} onClick={() => setSheet("time")} />
      <SummaryRow Icon={Flame} label="Warm-up · cool-down" value={describeWarmCool(userProfile)} onClick={() => setSheet("warmcool")} />
      {showAll && (
        <SummaryRow
          Icon={Pause}
          label="Pace & rest"
          value={describePaceAndRest(userProfile, isAuthenticated ? cloudProfile?.training_data?.pace : null)}
          onClick={() => setSheet("rest")}
        />
      )}
      {showAll && (
        <SummaryRow Icon={ArrowLeftRight} label="Changeovers" value={describeChangeovers(workoutSettings)} onClick={() => setSheet("changeovers")} />
      )}
      <SummaryRow
        Icon={NotebookPen}
        label="Preferences"
        value={userProfile.trainingPreferences || "None"}
        muted={!userProfile.trainingPreferences}
        onClick={() => setSheet("prefs")}
      />
      {showAll && (
        <SummaryRow Icon={History} label="Recent history" value={describeHistory(workoutSettings)} onClick={() => setSheet("history")} />
      )}
      {(showAll || hasRecipes) && (
      <SummaryRow
        Icon={BookMarked}
        label="Session recipes"
        value={recipes.length ? `${recipes.length} saved` : "None yet"}
        muted={!recipes.length}
        onClick={() => setSheet("recipes")}
      />
      )}
    </Group>
  );

  // Heart-rate zones (#94): what the zones are worked out from.
  const hrBasis = heartRateBasis(userProfile);

  const zone2 = hrBasis ? zoneFloors(hrBasis) : null;
  const strap = btItems.find((item) => item.type === "hr_monitor");
  const vo2Headline = vo2max.headline;
  // Your numbers: what you've measured or Khronos has worked out. Body &
  // heart is what you set about yourself.
  const yourNumbers = (
    <Group title="Your numbers" note="Measured or worked out">
      {(showAll || vo2Headline) && (
        <SummaryRow
          Icon={Wind}
          label="VO2max"
          value={
            vo2Headline
              ? `${hrBasis?.maxEstimated && vo2max.fromHeartRate ? "About " : ""}${vo2Headline} ml/kg/min`
              : "Not yet"
          }
          muted={!vo2Headline}
          onClick={() => setSheet("vo2")}
        />
      )}
      {benchmarks.length > 0 ? (
        <ItemRow
          Icon={Activity}
          title="Benchmarks"
          detail={previewBenchmarks(benchmarks)}
          tag={<Tag>{benchmarks.length}</Tag>}
          onClick={() => setSheet("benchmarks")}
        />
      ) : showAll ? (
        <AddRow label="Add a benchmark" onClick={() => setSheet("benchmarks")} />
      ) : null}
    </Group>
  );

  const openStrap = () => {
    setSettingsSheet({ ble: strap.key });
    setView("settings");
  };
  const bodyAndHeart = (
    <Group
      title="Body & heart"
      note="What you set about yourself"
      footer={!strap && eq.isSupported && eq.hrPairError ? eq.hrPairError : null}
    >
      <SummaryRow
        Icon={HeartPulse}
        label="Heart rate & zones"
        value={zone2 ? `Zone 2 · ${zone2[1]}–${zone2[2] - 1}` : "Not set"}
        muted={!hrBasis || (hrBasis.maxEstimated && hrBasis.restingEstimated)}
        onClick={() => setSheet("heart")}
      />
      <SummaryRow
        Icon={Scale}
        label="Body weight"
        value={userProfile.bodyWeightKg ? formatWeight(userProfile.bodyWeightKg, weightUnitOf(userProfile)) : "Not set"}
        muted={!userProfile.bodyWeightKg}
        onClick={() => setSheet("weight")}
      />
      {strap ? (
        <ItemRow
          Icon={Heart}
          title="Heart-rate strap"
          detail={`${strap.name || "Strap"} · ${strap.kind === "hr" ? "connected" : "not connected"}`}
          onClick={openStrap}
        />
      ) : eq.isSupported ? (
        <AddRow
          label={eq.hrPairing ? "Pairing…" : "Pair a heart-rate strap"}
          onClick={() => !eq.hrPairing && eq.addHrMonitor()}
        />
      ) : null}
    </Group>
  );

  const signInNote = !isAuthenticated && (
    <span>Sign in to save your own places and equipment sets.</span>
  );

  const whereYouTrain = (
    <>
      <Group title="Where you train" footer={signInNote}>
        {places.map((p) => (
          <ItemRow
            key={p.id}
            Icon={MapPin}
            title={p.name}
            detail={describePlace(p, kits)}
            tag={p.is_default && <DefaultTag />}
            onClick={() => setSheet({ place: p.id })}
          />
        ))}
        {isAuthenticated && <AddRow label="Add a place" onClick={() => setSheet({ place: "new" })} />}
      </Group>
      {isAuthenticated && (
        <Group title="Equipment sets" note="Link one to a place">
          {kits.map((k) => (
            <ItemRow
              key={k.id}
              Icon={Dumbbell}
              title={k.name}
              detail={describeEquipmentSet(k)}
              tag={k.is_default && <DefaultTag />}
              onClick={() => setSheet({ kit: k.id })}
            />
          ))}
          <AddRow label="Add an equipment set" onClick={() => setSheet({ kit: "new" })} />
        </Group>
      )}
    </>
  );

  return (
    <div className="mx-auto grid w-full max-w-5xl grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-2 lg:gap-x-8">
      <button
        type="button"
        onClick={() => setSheet("account")}
        className="grid grid-cols-[3rem_minmax(0,1fr)_1rem] items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:bg-muted/30 lg:col-span-2"
      >
        {user?.photoUrl ? (
          <img src={user.photoUrl} alt="" className="h-12 w-12 rounded-full object-cover" />
        ) : (
          <span className="grid h-12 w-12 place-items-center rounded-full border border-primary/40 bg-muted text-lg font-bold">
            {(name || "?").charAt(0).toUpperCase()}
          </span>
        )}
        <span className="min-w-0">
          <span className="block truncate text-base font-semibold">
            {isAuthenticated ? name || user?.email : "Not signed in"}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {isAuthenticated ? user?.email : "Saved on this device only"}
          </span>
        </span>
        <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
      </button>
      {/* Consistency level (api/lib/level.js), with badges. */}
      {isAuthenticated && badgesShown && <LevelCard level={streaks?.self?.level} />}

      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-6">
        {aboutYou}
        {howYouTrain}
      </div>
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-6">
        {bodyAndHeart}
        {(showAll || benchmarks.length > 0 || vo2Headline) && yourNumbers}
        {whereYouTrain}
        {!showAll && <MoreOptionsRow onClick={reveal} />}
        <button
          type="button"
          onClick={openSetup}
          className="justify-self-start px-0.5 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          Run getting started again
        </button>
      </div>

      <ProfileSettingsSheet kind={settingsKind} onClose={close} />
      <BenchmarksSheet open={sheet === "benchmarks"} startNew={benchmarks.length === 0} onClose={close} />
      <RecipesSheet open={sheet === "recipes"} onClose={close} />
      <Vo2maxSheet
        open={sheet === "vo2"}
        onClose={close}
        {...vo2max.sheet}
        onSetSex={() => setSheet("sex")}
        signedIn={isAuthenticated}
        onAddBenchmark={() => setSheet("benchmarks")}
        hasWeight={!!userProfile.bodyWeightKg}
        maxEstimated={!!hrBasis?.maxEstimated}
      />
      <PlaceSheet placeId={sheet?.place ?? null} onClose={close} />
      <EquipmentSetSheet setId={sheet?.kit ?? null} onClose={close} />
    </div>
  );
};

export default ProfileView;
